import { CommonModule } from '@angular/common';
import { Component, HostListener, inject } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { DsIconButtonV2Component } from '@customdesignsystem';
import { Util } from '@shared/common/utils/util';
import { FirestoreService } from '@services/firestore/firestore.service';
import { ModalService } from '@services/modal/modal.service';
import { Observable, map } from 'rxjs';

interface PiSolicitudPaso {
  title: string;
  description: string;
}

interface PiTramiteInvalidezConfig {
  videoRevisaPasos?: string;
}

/**
 * Modal “Pasos para presentar tu solicitud” — Figma (Revisa los pasos).
 */
@Component({
  selector: 'app-pi-modal-revisa-pasos',
  standalone: true,
  imports: [CommonModule, DsIconButtonV2Component],
  templateUrl: './pi-modal-revisa-pasos.component.html',
  styleUrls: ['./pi-modal-revisa-pasos.component.scss'],
})
export class PiModalRevisaPasosComponent {
  private readonly modalService = inject(ModalService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly firestoreService = inject(FirestoreService);
  private readonly util = inject(Util);

  isScrolled = false;

  readonly videoUrl$: Observable<SafeResourceUrl | null> = this.firestoreService
    .getDoc<PiTramiteInvalidezConfig>('configuraciones', 'tramite-pension-invalidez')
    .pipe(map((config) => this.toSafeYoutubeEmbed(config?.videoRevisaPasos)));

  readonly pasos: PiSolicitudPaso[] = [
    {
      title: 'Validación de identidad',
      description:
        'Confirma tu identidad con tu Clave de Seguridad, o recibe una Clave Dinámica por mensaje de texto o correo.',
    },
    {
      title: 'Tu información',
      description:
        'Revisa tus datos personales. Necesitarás tu certificado de nacimiento, de matrimonio o unión civil y tu cédula, según tu caso',
    },
    {
      title: 'Datos de empleador y licencia médica',
      description:
        'Comprueba la información de tu empleador. Y si tienes licencia médica, deberás completar algunos datos.',
    },
    {
      title: 'Cobertura de seguro',
      description: 'Revisa si tienes cobertura de Seguro de Invalidez y Sobrevivencia.',
    },
    {
      title: 'Antecedentes médicos',
      description:
        'Te pediremos información sobre tu enfermedad. Si no puedes comparecer, necesitarás un certificado médico para justificar ante la Comisión.',
    },
    {
      title: 'Antecedentes de pensión',
      description: 'Responde las preguntas relacionadas a tu pensión.',
    },
    {
      title: 'Información de pago',
      description: 'Completa los datos para recibir el pago de tu pensión.',
    },
    {
      title: 'Confirmación',
      description: 'Revisa que la información esté correcta y envía tu solicitud.',
    },
  ];

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    this.cerrar();
  }

  cerrar(): void {
    this.modalService.closeModal();
  }

  onScroll(event: Event): void {
    const target = event.target as HTMLElement;
    this.isScrolled = target.scrollTop > 0;
  }

  /**
   * Solo confía en URLs de embed construidas con un ID de YouTube validado.
   * No se marca como trusted el string crudo de Firestore (S6268).
   */
  private toSafeYoutubeEmbed(urlOrId?: string): SafeResourceUrl | null {
    const embedUrl = this.util.construirYoutubeEmbedUrl(urlOrId);
    if (!embedUrl) {
      return null;
    }

    // Safe: embedUrl is always https://www.youtube.com/embed/{validatedId}
    return this.sanitizer.bypassSecurityTrustResourceUrl(embedUrl); // NOSONAR
  }
}
